import React, { useEffect, useRef, useState } from 'react';
import { complaintsAPI } from '../services/api';
import { publicAssetPath } from '../utils/publicAssetPath';
import {
  FEEDBACK_INTRO_TEXT,
  SERVICE_TYPES,
  SURVEY_QUESTIONS,
  SurveyAnswerKey,
} from '../constants/surveyQuestions';

const SITE_URL = 'https://paytakht-mic.com/';

type Step =
  | 'intro'
  | 'type'
  | 'complaint'
  | 'survey_meta'
  | `survey_${number}`;

type FeedbackType = 'complaint' | 'survey' | null;

type SuccessInfo = {
  trackingCode?: string;
  type: 'complaint' | 'survey';
  message: string;
};

const PublicComplaintForm: React.FC = () => {
  const [step, setStep] = useState<Step>('intro');
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(null);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [surveyAnswers, setSurveyAnswers] = useState<Partial<Record<SurveyAnswerKey, string>>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SuccessInfo | null>(null);

  const [phoneKnown, setPhoneKnown] = useState(false);
  const [showNameField, setShowNameField] = useState(false);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const lookupRequestId = useRef(0);

  useEffect(() => {
    const phoneVal = phone.trim();
    if (!/^09\d{9}$/.test(phoneVal)) {
      setPhoneKnown(false);
      setShowNameField(false);
      setFullName('');
      setPhoneChecking(false);
      return;
    }

    const requestId = ++lookupRequestId.current;
    setPhoneChecking(true);
    setError(null);

    const timer = setTimeout(async () => {
      try {
        const res = await complaintsAPI.lookupPhone(phoneVal);
        if (lookupRequestId.current !== requestId) return;

        if (res.data.found && res.data.full_name) {
          setPhoneKnown(true);
          setShowNameField(false);
          setFullName(res.data.full_name);
        } else {
          setPhoneKnown(false);
          setShowNameField(true);
          setFullName('');
        }
      } catch {
        if (lookupRequestId.current !== requestId) return;
        setPhoneKnown(false);
        setShowNameField(true);
      } finally {
        if (lookupRequestId.current === requestId) {
          setPhoneChecking(false);
        }
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [phone]);

  const phoneValid = /^09\d{9}$/.test(phone.trim());
  const showPhoneSuccess = phoneValid && !phoneChecking;
  const phoneFieldIconPad = phoneChecking || showPhoneSuccess ? 'pr-11' : 'pr-4';

  const validateIntro = (): boolean => {
    const phoneVal = phone.trim();
    const name = fullName.trim();
    if (!/^09\d{9}$/.test(phoneVal)) {
      setError('شماره موبایل باید ۱۱ رقم و با ۰۹ شروع شود');
      return false;
    }
    if (phoneChecking) {
      setError('لطفاً چند لحظه صبر کنید تا شماره بررسی شود');
      return false;
    }
    if (!phoneKnown && name.length < 2) {
      setError('لطفاً نام و نام خانوادگی را وارد کنید');
      return false;
    }
    setError(null);
    return true;
  };

  const handleIntroNext = () => {
    if (validateIntro()) setStep('type');
  };

  const handleSelectType = (type: FeedbackType) => {
    setFeedbackType(type);
    setError(null);
    if (type === 'complaint') setStep('complaint');
    else setStep('survey_meta');
  };

  const surveyQuestionIndex =
    step.startsWith('survey_') && step !== 'survey_meta'
      ? Number(step.replace('survey_', ''))
      : -1;

  const totalSurveySteps = 1 + SURVEY_QUESTIONS.length;
  const currentSurveyStepNumber =
    step === 'survey_meta'
      ? 1
      : surveyQuestionIndex >= 0
        ? surveyQuestionIndex + 2
        : 0;

  const handleSurveyMetaNext = () => {
    if (!serviceType) {
      setError('نوع خدمت را انتخاب کنید');
      return;
    }
    if (visitDate.trim().length < 4) {
      setError('تاریخ مراجعه را وارد کنید');
      return;
    }
    setError(null);
    setStep('survey_0');
  };

  const handleSurveyAnswer = (questionId: SurveyAnswerKey, value: string) => {
    setSurveyAnswers((prev) => ({ ...prev, [questionId]: value }));
    setError(null);
  };

  const handleSurveyQuestionNext = () => {
    if (surveyQuestionIndex < 0) return;
    const q = SURVEY_QUESTIONS[surveyQuestionIndex];
    if (!surveyAnswers[q.id]) {
      setError('لطفاً یک گزینه را انتخاب کنید');
      return;
    }
    setError(null);
    if (surveyQuestionIndex < SURVEY_QUESTIONS.length - 1) {
      setStep(`survey_${surveyQuestionIndex + 1}` as Step);
    } else {
      void submitSurvey();
    }
  };

  const handleSurveyBack = () => {
    setError(null);
    if (surveyQuestionIndex === 0) setStep('survey_meta');
    else if (surveyQuestionIndex > 0) setStep(`survey_${surveyQuestionIndex - 1}` as Step);
    else setStep('type');
  };

  const submitComplaint = async () => {
    const desc = description.trim();
    if (desc.length < 10) {
      setError('شرح شکایت باید حداقل ۱۰ کاراکتر باشد');
      return;
    }
    if (!validateIntro()) return;

    setLoading(true);
    setError(null);
    try {
      const res = await complaintsAPI.create({
        submission_type: 'complaint',
        phone: phone.trim(),
        description: desc,
        ...(phoneKnown ? {} : { full_name: fullName.trim() }),
      });
      setSuccess({
        trackingCode: res.data.tracking_code,
        type: 'complaint',
        message: res.data.message,
      });
    } catch (err: unknown) {
      setError(extractApiError(err, 'خطا در ثبت شکایت. لطفاً دوباره تلاش کنید.'));
    } finally {
      setLoading(false);
    }
  };

  const submitSurvey = async () => {
    if (!validateIntro()) return;
    if (!serviceType || visitDate.trim().length < 4) {
      setError('اطلاعات نظرسنجی ناقص است');
      return;
    }
    for (const q of SURVEY_QUESTIONS) {
      if (!surveyAnswers[q.id]) {
        setError('لطفاً به همه سوالات پاسخ دهید');
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const res = await complaintsAPI.create({
        submission_type: 'survey',
        phone: phone.trim(),
        ...(phoneKnown ? {} : { full_name: fullName.trim() }),
        survey_answers: {
          service_type: serviceType,
          visit_date: visitDate.trim(),
          ...surveyAnswers,
        } as Record<string, string>,
      });
      setSuccess({
        type: 'survey',
        message: res.data.message,
      });
    } catch (err: unknown) {
      setError(extractApiError(err, 'خطا در ثبت نظرسنجی. لطفاً دوباره تلاش کنید.'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white shadow-xl rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {success.type === 'complaint' ? 'شکایت شما ثبت شد' : 'نظرسنجی شما ثبت شد'}
          </h1>
          {success.type === 'complaint' && success.trackingCode && (
            <>
              <p className="text-sm text-gray-600 mb-4">شماره پیگیری شما:</p>
              <p className="text-2xl font-bold text-blue-700 tracking-widest mb-6" dir="ltr">
                {success.trackingCode}
              </p>
            </>
          )}
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">{success.message}</p>
          <a
            href={SITE_URL}
            className="inline-block w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            برگشت به سایت
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-6 flex flex-col items-center">
          <img src={publicAssetPath('/logo.png')} alt="لوگو" className="w-20 h-20 object-contain mb-3" />
          <h1 className="text-2xl font-bold text-gray-900">انتقادات و پیشنهادات</h1>
        </div>

        <div className="bg-white shadow-xl rounded-2xl p-6">
          {step === 'intro' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed text-justify bg-blue-50/60 border border-blue-100 rounded-xl p-4">
                {FEEDBACK_INTRO_TEXT}
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">شماره موبایل</label>
                <PhoneInput
                  phone={phone}
                  onPhoneChange={setPhone}
                  phoneFieldIconPad={phoneFieldIconPad}
                  showPhoneSuccess={showPhoneSuccess}
                  phoneChecking={phoneChecking}
                />
              </div>

              {showNameField && !phoneChecking && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    نام و نام خانوادگی
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="مثال: علی محمدی"
                    required
                  />
                </div>
              )}

              {error && <ErrorBox message={error} />}

              <button
                type="button"
                onClick={handleIntroNext}
                disabled={phoneChecking}
                className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                بعدی
              </button>
            </div>
          )}

          {step === 'type' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center mb-2">لطفاً نوع ثبت را انتخاب کنید</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TypeCard
                  title="نظرسنجی"
                  description="ارزیابی کیفیت خدمات دریافت‌شده"
                  selected={feedbackType === 'survey'}
                  onClick={() => handleSelectType('survey')}
                  icon="📋"
                />
                <TypeCard
                  title="شکایت"
                  description="ثبت نارضایتی یا مشکل"
                  selected={feedbackType === 'complaint'}
                  onClick={() => handleSelectType('complaint')}
                  icon="⚠️"
                />
              </div>
              {error && <ErrorBox message={error} />}
              <button
                type="button"
                onClick={() => setStep('intro')}
                className="w-full py-2.5 text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                قبلی
              </button>
            </div>
          )}

          {step === 'complaint' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">شرح شکایت</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                  placeholder="مشکل یا نارضایتی خود را بنویسید..."
                />
              </div>
              {error && <ErrorBox message={error} />}
              <button
                type="button"
                onClick={() => void submitComplaint()}
                disabled={loading}
                className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {loading ? 'در حال ارسال...' : 'ارسال شکایت'}
              </button>
              <button
                type="button"
                onClick={() => setStep('type')}
                className="w-full py-2.5 text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                قبلی
              </button>
            </div>
          )}

          {step === 'survey_meta' && (
            <div className="space-y-4">
              <SurveyProgress current={1} total={totalSurveySteps} />
              <h2 className="font-semibold text-gray-900">اطلاعات مراجعه</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  نوع خدمت دریافت‌شده
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {SERVICE_TYPES.map((type) => (
                    <OptionButton
                      key={type}
                      label={type}
                      selected={serviceType === type}
                      onClick={() => {
                        setServiceType(type);
                        setError(null);
                      }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">تاریخ مراجعه</label>
                <input
                  type="text"
                  value={visitDate}
                  onChange={(e) => setVisitDate(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="مثال: ۱۴۰۴/۰۳/۱۵"
                />
              </div>

              {error && <ErrorBox message={error} />}

              <button
                type="button"
                onClick={handleSurveyMetaNext}
                className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
              >
                بعدی
              </button>
              <button
                type="button"
                onClick={() => setStep('type')}
                className="w-full py-2.5 text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                قبلی
              </button>
            </div>
          )}

          {surveyQuestionIndex >= 0 && (
            <div className="space-y-4">
              <SurveyProgress current={currentSurveyStepNumber} total={totalSurveySteps} />
              <h2 className="font-semibold text-gray-900 leading-relaxed">
                {SURVEY_QUESTIONS[surveyQuestionIndex].title}
              </h2>
              <div className="space-y-2">
                {SURVEY_QUESTIONS[surveyQuestionIndex].options.map((opt) => (
                  <OptionButton
                    key={opt}
                    label={opt}
                    selected={surveyAnswers[SURVEY_QUESTIONS[surveyQuestionIndex].id] === opt}
                    onClick={() =>
                      handleSurveyAnswer(SURVEY_QUESTIONS[surveyQuestionIndex].id, opt)
                    }
                  />
                ))}
              </div>
              {error && <ErrorBox message={error} />}
              <button
                type="button"
                onClick={handleSurveyQuestionNext}
                disabled={loading}
                className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {loading
                  ? 'در حال ارسال...'
                  : surveyQuestionIndex === SURVEY_QUESTIONS.length - 1
                    ? 'ارسال نظرسنجی'
                    : 'بعدی'}
              </button>
              <button
                type="button"
                onClick={handleSurveyBack}
                disabled={loading}
                className="w-full py-2.5 text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                قبلی
              </button>
            </div>
          )}

          <div className="mt-4 text-center">
            <a href={SITE_URL} className="text-sm text-gray-500 hover:text-gray-700">
              برگشت به سایت
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

function extractApiError(err: unknown, fallback: string): string {
  const detail =
    (err as { response?: { data?: { detail?: string | { msg?: string }[] } } })?.response?.data
      ?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
}

const ErrorBox: React.FC<{ message: string }> = ({ message }) => (
  <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{message}</p>
);

const SurveyProgress: React.FC<{ current: number; total: number }> = ({ current, total }) => (
  <div>
    <div className="flex justify-between text-xs text-gray-500 mb-1.5">
      <span>ارزیابی کیفیت خدمات</span>
      <span>
        {current} از {total}
      </span>
    </div>
    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div
        className="h-full bg-blue-600 transition-all duration-300 rounded-full"
        style={{ width: `${(current / total) * 100}%` }}
      />
    </div>
  </div>
);

const OptionButton: React.FC<{
  label: string;
  selected: boolean;
  onClick: () => void;
}> = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-right px-4 py-3 rounded-xl border text-sm font-medium transition-colors ${
      selected
        ? 'border-blue-600 bg-blue-50 text-blue-800 ring-2 ring-blue-500/20'
        : 'border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-gray-50'
    }`}
  >
    <span className="inline-flex items-center gap-2">
      <span
        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
          selected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
        }`}
      >
        {selected && (
          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
            <path d="M10.28 2.28a.75.75 0 00-1.06-1.06L4.5 6.94 2.78 5.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.06 0l5.25-5.25z" />
          </svg>
        )}
      </span>
      {label}
    </span>
  </button>
);

const TypeCard: React.FC<{
  title: string;
  description: string;
  icon: string;
  selected: boolean;
  onClick: () => void;
}> = ({ title, description, icon, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-right p-4 rounded-xl border-2 transition-all ${
      selected
        ? 'border-blue-600 bg-blue-50 shadow-sm'
        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
    }`}
  >
    <span className="text-2xl mb-2 block">{icon}</span>
    <span className="font-semibold text-gray-900 block">{title}</span>
    <span className="text-xs text-gray-500 mt-1 block">{description}</span>
  </button>
);

const PhoneInput: React.FC<{
  phone: string;
  onPhoneChange: (v: string) => void;
  phoneFieldIconPad: string;
  showPhoneSuccess: boolean;
  phoneChecking: boolean;
}> = ({ phone, onPhoneChange, phoneFieldIconPad, showPhoneSuccess, phoneChecking }) => (
  <div className="relative">
    <input
      type="tel"
      dir="ltr"
      value={phone}
      onChange={(e) => onPhoneChange(e.target.value.replace(/\D/g, '').slice(0, 11))}
      className={`w-full py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-left pl-4 transition-colors duration-300 ${phoneFieldIconPad} ${
        showPhoneSuccess ? 'border-emerald-400 bg-emerald-50/40' : 'border-gray-200'
      }`}
      placeholder="09123456789"
      maxLength={11}
      required
      autoComplete="tel"
    />
    {phoneChecking && (
      <span
        className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 pointer-events-none"
        aria-hidden
      >
        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </span>
    )}
    {showPhoneSuccess && (
      <span className="absolute right-3 top-1/2 animate-phone-check-pop pointer-events-none" aria-hidden>
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 animate-phone-check-ring">
          <svg
            className="w-3.5 h-3.5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        </span>
      </span>
    )}
  </div>
);

export default PublicComplaintForm;
